/**
 * Sample Record Detail Page
 * Shows sample record details and allows editing.
 */
import { useEffect, useState } from 'react';
import { useParams, useLocation } from 'wouter';
import { useSampleRecordDetail, useSampleRecordUpdate, type SampleRecordView } from '@/hooks/useLab';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { canReadSampleRecord, canWriteSampleRecord } from '@/lib/labPermissions';

type SampleStatus = 'collected' | 'received' | 'rejected' | 'retained' | 'discarded';

const STATUS_LABELS: Record<SampleStatus, string> = {
  collected: '已采集',
  received: '已接收',
  rejected: '已拒收',
  retained: '已保留',
  discarded: '已销毁',
};

interface SampleRecordFormData {
  sampleNo: string;
  sampleType: string;
  status: SampleStatus;
  collectedAmount?: string;
  collectedUnit: string;
}

export default function SampleRecordDetailPage() {
  const { recordId } = useParams<{ recordId: string }>();
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const { fetch: fetchDetail, loading: detailLoading } = useSampleRecordDetail(recordId!);
  const { update: updateRecord, loading: updateLoading } = useSampleRecordUpdate(recordId!);

  const [record, setRecord] = useState<SampleRecordView | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<SampleRecordFormData>({
    sampleNo: '',
    sampleType: '',
    status: 'collected',
    collectedAmount: '',
    collectedUnit: 'ml',
  });

  // Check permission — wildcard-aware (mirrors backend RBAC)
  if (!canReadSampleRecord(permissions)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="p-6 text-center">
          <h2 className="text-lg font-semibold">权限不足</h2>
          <p className="text-sm text-muted-foreground">您没有查看样本记录的权限</p>
        </Card>
      </div>
    );
  }

  // Load sample record detail
  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await fetchDetail();
        if (data) {
          setRecord(data);
          setFormData({
            sampleNo: data.sampleNo,
            sampleType: data.sampleType,
            status: data.status,
            collectedAmount: data.collectedAmount,
            collectedUnit: data.collectedUnit || 'ml',
          });
        }
      } catch (err) {
        toast.error(err instanceof Error ? err.message : '加载样本记录失败');
      }
    };
    loadData();
  }, [fetchDetail]);

  const handleSave = async () => {
    if (!canWriteSampleRecord(permissions)) {
      toast.error('您没有编辑样本记录的权限');
      return;
    }

    if (!record) {
      toast.error('样本记录未加载');
      return;
    }

    try {
      const updated = await updateRecord({
        sampleNo: formData.sampleNo,
        sampleType: formData.sampleType,
        status: formData.status,
        collectedAmount: formData.collectedAmount,
        collectedUnit: formData.collectedUnit,
        expectedVersion: record.version,
      });
      if (updated) {
        setRecord(updated);
        setFormData({
          sampleNo: updated.sampleNo,
          sampleType: updated.sampleType,
          status: updated.status,
          collectedAmount: updated.collectedAmount,
          collectedUnit: updated.collectedUnit || 'ml',
        });
        setIsEditing(false);
        toast.success('样本记录已更新');
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : '更新样本记录失败';
      toast.error(errorMsg);
    }
  };

  if (detailLoading) {
    return <div className="flex min-h-screen items-center justify-center">加载中...</div>;
  }

  if (!record) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="p-6 text-center">
          <h2 className="text-lg font-semibold">未找到样本记录</h2>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">样本记录详情</h1>
        <div className="space-x-2">
          {!isEditing && canWriteSampleRecord(permissions) && (
            <Button onClick={() => setIsEditing(true)}>编辑</Button>
          )}
          {isEditing && (
            <>
              <Button onClick={handleSave} disabled={updateLoading}>
                {updateLoading ? '保存中...' : '保存'}
              </Button>
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                取消
              </Button>
            </>
          )}
          <Button variant="outline" onClick={() => navigate('/lab/sample-records')}>
            返回列表
          </Button>
        </div>
      </div>

      <Card className="p-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <label className="text-sm font-semibold text-muted-foreground">样本编号</label>
            {isEditing ? (
              <Input
                value={formData.sampleNo}
                onChange={(e) => setFormData({ ...formData, sampleNo: e.target.value })}
                className="mt-1"
              />
            ) : (
              <p className="mt-1 text-lg font-mono">{record.sampleNo}</p>
            )}
          </div>

          <div>
            <label className="text-sm font-semibold text-muted-foreground">样本类型</label>
            {isEditing ? (
              <Input
                value={formData.sampleType}
                onChange={(e) => setFormData({ ...formData, sampleType: e.target.value })}
                className="mt-1"
              />
            ) : (
              <p className="mt-1">{record.sampleType}</p>
            )}
          </div>

          <div>
            <label className="text-sm font-semibold text-muted-foreground">采集量</label>
            {isEditing ? (
              <div className="mt-1 flex gap-2">
                <Input
                  type="number"
                  value={formData.collectedAmount || ''}
                  onChange={(e) =>
                    setFormData({ ...formData, collectedAmount: e.target.value })
                  }
                  className="flex-1"
                />
                <Input
                  value={formData.collectedUnit}
                  onChange={(e) =>
                    setFormData({ ...formData, collectedUnit: e.target.value })
                  }
                  className="w-24"
                />
              </div>
            ) : (
              <p className="mt-1">
                {record.collectedAmount} {record.collectedUnit}
              </p>
            )}
          </div>

          <div>
            <label className="text-sm font-semibold text-muted-foreground">状态</label>
            {isEditing ? (
              <select
                className="mt-1 w-full rounded border border-input bg-background px-3 py-2 text-sm"
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value as SampleStatus })
                }
              >
                <option value="collected">已采集</option>
                <option value="received">已接收</option>
                <option value="rejected">已拒收</option>
                <option value="retained">已保留</option>
                <option value="discarded">已销毁</option>
              </select>
            ) : (
              <p className="mt-1">{STATUS_LABELS[record.status]}</p>
            )}
          </div>

          <div>
            <label className="text-sm font-semibold text-muted-foreground">版本</label>
            <p className="mt-1 text-sm font-mono">{record.version}</p>
          </div>
        </div>
      </Card>
    </div>
  );
}
